import { useMutation } from '@apollo/client'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@chain-zen/ui'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { ENABLE_FOUR_KITES_CARRIER_APP } from '../../../data/mutations/apps'
import type { TCustomer } from '../../../types/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'
import { FourKitesCarrierUpdateModal } from './four-kites-carrier-update-modal'
import { getGroupStructure, getPayloadStructure } from './utils'

type TFieldValues = {
  [key: string]: string
}

interface ICustomField {
  disabled?: boolean
  integrationConfigurationID?: string
  isRequired?: boolean
  labelText: string
  name: string
  placeholder?: string
}

type TFourKitesCarrierEnableAndUpdateAppInput = {
  apiKey: string
  appType: AppTypeEnum
  customers: TCustomer[]
}

type TCustomerGroups = Record<string, ICustomField[]>

/**
 * Modal for enabling the Four Kites Carrier app
 * Allows entering API key and customer IDs with validation
 */
export function FourKitesCarrierModal({
  appType,
  isOpen,
  isModalEditable,
  setModalEditable,
  app,
  onClose
}: AppModalProps) {
  const [formError, setFormError] = useState('')
  const [customers, setCustomers] = useState<TCustomerGroups>(() => getGroupStructure(1))
  const [deletedGroups, setDeletedGroups] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<TFieldValues>({
    defaultValues: {
      apiKey: ''
    }
  })

  // Methods for managing customer fields
  const removeFieldsFromSchema = (id: string) => {
    setDeletedGroups((prev) => [...prev, id])
  }

  const handleAddMore = () => {
    const uniqueID = uuidv4()
    const newlyAddedGroup = getGroupStructure(uniqueID)
    setCustomers((old) => {
      return {
        ...old,
        ...newlyAddedGroup
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    reset()
    onClose()
    if (setModalEditable) {
      setModalEditable(false)
    }
  }

  const handleGroupDelete = (groupID: string) => {
    if (Object.keys(customers).length > 1) {
      removeFieldsFromSchema(groupID)
      setCustomers((old) => {
        const newGroups = { ...old }
        delete newGroups[groupID]
        return newGroups
      })
    }
  }

  // Mutation for enabling Four Kites Carrier app
  const [enableFourKitesCarrierApp, { loading }] = useMutation<
    { fourKitesCarrierEnableAndUpdateApp: { appEnabled: boolean } },
    TFourKitesCarrierEnableAndUpdateAppInput
  >(ENABLE_FOUR_KITES_CARRIER_APP, {
    onCompleted: () => {
      toast.success('Four Kites Carrier app is enabled successfully!')
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    }
  })

  // Handler for form submission
  const handleSaveDetails = (data: TFieldValues) => {
    // Create a copy of the data to avoid modifying the original
    const formData = { ...data }

    // Remove deleted fields from the copy
    deletedGroups.forEach((id) => {
      delete formData[`customerID-${id}`]
      delete formData[`customerIDInChaine-${id}`]
      delete formData[`customerNameInChaine-${id}`]
    })

    const payload = getPayloadStructure(formData)

    enableFourKitesCarrierApp({
      variables: {
        apiKey: payload.apiKey,
        appType: appType,
        customers: payload.customers
      }
    })
  }

  // If app is in edit mode and we have app data, render the UpdateModal instead
  if (isModalEditable && app) {
    return <FourKitesCarrierUpdateModal appType={appType} isOpen={isOpen} onClose={onClose} app={app} />
  }

  const getCustomerFields = () => {
    return Object.entries(customers).map(([groupId, fields]) => (
      <div key={groupId} className="bg-muted p-4 rounded-lg mb-4" role="group" aria-labelledby={`group-${groupId}`}>
        <div className="flex justify-end mb-2">
          {Object.keys(customers).length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleGroupDelete(groupId)}
              aria-label={`Remove customer group ${groupId}`}
            >
              Remove
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {fields.map((field: ICustomField) => {
            const errorId = errors[field.name] ? `${field.name}-error` : undefined
            return (
              <div key={field.name}>
                <Label htmlFor={field.name} className="block text-sm font-medium mb-1">
                  {field.labelText}{' '}
                  {!!field.isRequired && (
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                  )}
                </Label>
                <Input
                  id={field.name}
                  type="text"
                  placeholder={field.placeholder}
                  disabled={field.disabled || false}
                  aria-required={field.isRequired ? 'true' : 'false'}
                  aria-describedby={errorId}
                  aria-invalid={!!errors[field.name]}
                  {...register(field.name, {
                    required: field.isRequired ? `${field.labelText} is required` : false
                  })}
                />
                {!!errors[field.name] && (
                  <p className="text-sm text-red-500 mt-1" id={errorId} role="alert">
                    {errors[field.name]?.message}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>
    ))
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable Four Kites for Chain</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Connect your Four Kites account to enable tracking integration.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleSaveDetails)} className="space-y-4" noValidate={true}>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="apiKey" id="apiKey-label">
                API Key
              </Label>
              <Input
                id="apiKey"
                placeholder="Enter your Four Kites API key"
                aria-required="true"
                aria-describedby={errors.apiKey ? 'apiKey-error' : undefined}
                aria-invalid={!!errors.apiKey}
                {...register('apiKey', { required: 'API key is required' })}
              />
              {errors.apiKey?.message ? (
                <p className="text-sm text-red-500" id="apiKey-error" role="alert">
                  {errors.apiKey.message}
                </p>
              ) : null}
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="block text-sm font-medium">
                  Customer IDs{' '}
                  <span className="text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMore}
                  aria-label="Add another customer"
                >
                  Add More
                </Button>
              </div>

              {getCustomerFields()}
            </div>
          </div>

          <DialogFooter className="flex-col space-y-2 sm:space-y-0">
            {!!formError && (
              <p className="text-sm text-red-500 self-start" role="alert">
                {formError}
              </p>
            )}
            <div className="flex items-center space-x-2">
              <p className="text-sm text-muted-foreground">
                See{' '}
                <a
                  href="https://handbook.chaineapp.com/product-guide/apps/four-kites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary"
                  aria-label="Read the Four Kites integration guide (opens in a new tab)"
                >
                  detailed steps here.
                </a>
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || isSubmitting}>
                {loading || isSubmitting ? 'Enabling...' : 'Enable'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
